// Shared CSV cell escaping.
//
// CyberLab pentest AD25996 (September 2026), "Unfiltered User Input": every
// hand-built CSV export in the app escaped the CSV-structural characters
// (quotes, commas, newlines) but never neutralised a cell that opens as a
// spreadsheet formula. A free-text field one person types (a client name, a
// note, a bid title) is then run as a formula when somebody else opens the
// export in Excel.
//
// Follows OWASP's CSV injection guidance: a string cell starting with =, +,
// -, @, a tab or a carriage return gets a leading single quote so Excel reads
// it as text rather than a formula. Numbers are exempt, including a numeric
// string produced by stringifying a number (e.g. "-12.50"), so a negative
// amount in a finance export still reads as a negative amount.
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;
const PLAIN_NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)$/;

/** One CSV cell: guard a formula-triggering string, then quote-escape it. */
export function csvCell(value) {
  if (value == null) return '';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  let s = String(value);
  if (FORMULA_TRIGGER.test(s) && !PLAIN_NUMBER.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** One CSV line from an array of raw cell values. */
export const csvRow = (values) => values.map(csvCell).join(',');

/** A full CSV document (no trailing newline) from a header row and data rows. */
export const toCsv = (header, rows) => [csvRow(header), ...rows.map(csvRow)].join('\n');

/**
 * A value made safe to open in a spreadsheet, without any CSV quoting: the same
 * formula guard as csvCell. For text that reaches Excel some other way, such as
 * the clipboard (pen test AD26341, finding AD11, retest).
 */
export function formulaSafe(value) {
  if (value == null) return '';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  const s = String(value);
  return FORMULA_TRIGGER.test(s) && !PLAIN_NUMBER.test(s) ? `'${s}` : s;
}

/**
 * One tab-separated cell, as pasted into a spreadsheet: formula-guarded, and with
 * any tab or line break inside it turned into a space, so a cell can neither start
 * a formula nor spill into the next column or row.
 */
export const tsvCell = (value) => formulaSafe(value).replace(/[\t\r\n]+/g, ' ');

/** Text made safe to place in HTML, as element content or a quoted attribute. */
export const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
